import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faChartBar, 
    faPhone, 
    faClock, 
    faCheckCircle, 
    faExclamationTriangle,
    faClipboardList,
    faTasks,
    faArrowUp,
    faArrowDown,
    faMinus,
    faClipboardCheck,
    faStar,
    faInfoCircle,
    faChevronDown,
    faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';

const CatiInterviewerDashboard = () => {
    const [dashboardData, setDashboardData] = useState({
        productivity: {
            totalCompletes: 0,
            todayCompletes: 0,
            weekCompletes: 0,
            monthCompletes: 0,
            averageDaily: 0,
            lastWeekTrend: 0
        },
        assignment: {
            currentPost: null,
            currentSurvey: null,
            startDate: null,
            hoursWorked: 0,
            status: 'active'
        },
        performance: {
            rank: 0,
            totalInterviewers: 0,
            completionRate: 0,
            averageTime: 0,
            earnings: 0
        },
        recentActivity: [],
        interviewLines: {
            total: 0,
            completed: 0,
            pending: 0
        }
    });
    const [interviewer, setInterviewer] = useState(null);
    const [controlRatings, setControlRatings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showScoreDetails, setShowScoreDetails] = useState(false);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError('');

            // Fetch interviewer profile data
            try {
                const profileResponse = await api.get('/interviewer/profile');
                if (profileResponse.data.success) {
                    setInterviewer(profileResponse.data.data);
                }
            } catch (profileError) {
                console.error('Error fetching interviewer profile:', profileError);
            }
            
            // Fetch control ratings
            try {
                const ratingsResponse = await api.get('/interviewer/control-ratings');
                if (ratingsResponse.data.success) {
                    setControlRatings(ratingsResponse.data.data);
                }
            } catch (ratingsError) {
                console.error('Error fetching control ratings:', ratingsError);
            }

            // Fetch all dashboard data in one API call
            const response = await api.get('/cati/interviewer/dashboard');

            if (response.data.success) {
                setDashboardData(response.data.data);
            } else {
                setError(response.data.message || 'Erreur lors du chargement des données du tableau de bord');
            }

        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setError('Erreur lors du chargement des données du tableau de bord');
        } finally {
            setLoading(false);
        }
    };

    const getTrendIcon = (trend) => {
        if (trend > 0) return faArrowUp;
        if (trend < 0) return faArrowDown;
        return faMinus;
    };

    const getTrendColor = (trend) => {
        if (trend > 0) return 'text-green-600';
        if (trend < 0) return 'text-red-600';
        return 'text-gray-600';
    };

    const renderStarRating = (rating, rawRating, totalChecks, showTooltip = true) => {
        // Always use rawRating if available (it's the actual 1-5 rating from database)
        // Only fall back to percentage conversion if rawRating is missing
        let numRating;
        if (rawRating !== null && rawRating !== undefined && !isNaN(rawRating) && Number(rawRating) > 0) {
            numRating = Number(rawRating);
        } else if (rating !== null && rating !== undefined && !isNaN(rating)) {
            // Convert percentage (0-100) to 1-5 scale as fallback
            numRating = (Number(rating) / 100) * 5;
        } else {
            return (
                <div className="flex items-center gap-1">
                    <span className="text-gray-400 text-sm">N/A</span>
                </div>
            );
        }

        // Ensure rating is between 0 and 5
        numRating = Math.max(0, Math.min(5, numRating));
        
        // Render 5 stars with accurate partial filling
        const stars = [];
        for (let i = 0; i < 5; i++) {
            const starValue = numRating - i;
            let fillPercentage = 0;
            
            if (starValue >= 1) {
                fillPercentage = 100; // Fully filled
            } else if (starValue > 0) {
                fillPercentage = starValue * 100; // Partially filled based on exact percentage
            }
            
            stars.push(
                <span 
                    key={i} 
                    className="relative inline-block" 
                    style={{ width: '14px', height: '14px', display: 'inline-block', verticalAlign: 'middle' }}
                >
                    {/* Background gray star - always full */}
                    <FontAwesomeIcon 
                        icon={faStar} 
                        className="text-gray-300 text-sm"
                        style={{ position: 'absolute', left: 0, top: 0, width: '14px', height: '14px' }}
                    />
                    {/* Foreground red star - clipped based on fill percentage */}
                    {fillPercentage > 0 && (
                        <div
                            className="absolute top-0 left-0 overflow-hidden"
                            style={{
                                width: `${fillPercentage}%`,
                                height: '100%'
                            }}
                        >
                            <FontAwesomeIcon 
                                icon={faStar} 
                                className="text-red-600 text-sm"
                                style={{ width: '14px', height: '14px', display: 'block' }}
                            />
                        </div>
                    )}
                </span>
            );
        }

        // Calculate the display value
        const displayValue = rawRating !== null && rawRating !== undefined && !isNaN(rawRating) 
            ? Number(rawRating).toFixed(1) 
            : (rating ? ((Number(rating) / 100) * 5).toFixed(1) : 'N/A');

        return (
            <div className="relative group" style={{ zIndex: 1 }}>
                <div className="flex items-center gap-0.5">
                    {stars}
                </div>
                
                {/* Simple tooltip on hover - just the star value */}
                {showTooltip && (
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block pointer-events-none whitespace-nowrap" style={{ zIndex: 10000 }}>
                        <div className="bg-gray-900 text-white text-xs rounded py-1.5 px-2 shadow-lg relative">
                            Score de contrôle: {displayValue}/5
                            {/* Arrow pointing down */}
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-px">
                                <div className="border-4 border-transparent border-t-gray-900"></div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const formatDuration = (hours) => {
        if (hours < 1) return `${Math.round(hours * 60)} min`;
        return `${hours.toFixed(1)}h`;
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-MA', {
            style: 'currency',
            currency: 'MAD',
            minimumFractionDigits: 0
        }).format(amount);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
                        backgroundSize: '20px 20px'
                    }}></div>
                </div>
                <div className="text-center relative z-10">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Chargement du tableau de bord CATI...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-red-500 mb-4" />
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button 
                        onClick={fetchDashboardData}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                        Réessayer
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 relative overflow-hidden">
            {/* Dotted Background */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                    backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                }}></div>
            </div>

            <div className="relative z-10 p-6">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="mb-8">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
                                    Tableau de Bord CATI
                                </h1>
                                <p className="text-gray-600">Suivez votre performance et vos enquêtes téléphoniques</p>
                            </div>
                        </div>
                    </div>

                    {/* Main Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                        
                        {/* Productivity Overview - Large Card */}
                        <div className="lg:col-span-8 bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold text-gray-900">Productivité</h2>
                                <FontAwesomeIcon icon={faChartBar} className="text-2xl text-red-600" />
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                                <div className="text-center p-6 bg-white border border-gray-200 rounded-lg">
                                    <div className="text-3xl font-bold text-gray-900">
                                        {dashboardData.productivity.totalCompletes}
                                    </div>
                                    <div className="text-sm text-gray-600 mt-1">Total Complétés</div>
                                </div>
                                <div className="text-center p-6 bg-white border border-gray-200 rounded-lg">
                                    <div className="text-3xl font-bold text-green-600">
                                        {dashboardData.productivity.todayCompletes}
                                    </div>
                                    <div className="text-sm text-gray-600 mt-1">Aujourd'hui</div>
                                </div>
                                <div className="text-center p-6 bg-white border border-gray-200 rounded-lg">
                                    <div className="text-3xl font-bold text-blue-600">
                                        {dashboardData.productivity.weekCompletes}
                                    </div>
                                    <div className="text-sm text-gray-600 mt-1">Cette Semaine</div>
                                </div>
                                <div className="text-center p-6 bg-white border border-gray-200 rounded-lg">
                                    <div className="text-3xl font-bold text-purple-600">
                                        {dashboardData.productivity.monthCompletes}
                                    </div>
                                    <div className="text-sm text-gray-600 mt-1">Ce Mois</div>
                                </div>
                            </div>

                            {/* Performance Metrics */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="p-6 bg-white border border-gray-200 rounded-lg">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-gray-600">Moyenne Quotidienne</p>
                                            <p className="text-2xl font-bold text-gray-900 mt-1">
                                                {dashboardData.productivity.averageDaily}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-6 bg-white border border-gray-200 rounded-lg">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-gray-600">Tendance Semaine</p>
                                            <div className="flex items-center space-x-2 mt-1">
                                                <FontAwesomeIcon 
                                                    icon={getTrendIcon(dashboardData.productivity.lastWeekTrend)} 
                                                    className={`text-lg ${getTrendColor(dashboardData.productivity.lastWeekTrend)}`}
                                                />
                                                <span className="text-2xl font-bold text-gray-900">
                                                    {Math.abs(dashboardData.productivity.lastWeekTrend)}%
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Current Assignment */}
                        <div className="lg:col-span-4 bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                            <h3 className="text-xl font-bold text-gray-900 mb-6">Poste</h3>
                            
                            {(() => {
                                const postToShow = dashboardData.assignment.currentPost || dashboardData.assignment.dedicatedPost;
                                const isDedicated = dashboardData.assignment.dedicatedPost && 
                                    (!dashboardData.assignment.currentPost || 
                                     dashboardData.assignment.currentPost.id === dashboardData.assignment.dedicatedPost.id);
                                const isLargeNumber = postToShow && postToShow.number && postToShow.number.toString().length > 8;
                                
                                if (postToShow) {
                                    return (
                                <div className="text-center py-8">
                                    <div className="mb-6">
                                                <div className="text-sm text-gray-600 mb-2">
                                                    {isDedicated ? 'Poste Dédié' : 'Poste Actuel'}
                                                </div>
                                                {isLargeNumber ? (
                                                    <div className="text-3xl font-bold text-red-600 break-all px-2">
                                                        #{postToShow.number}
                                                    </div>
                                                ) : (
                                        <div className="text-6xl font-bold text-red-600">
                                                        #{postToShow.number}
                                        </div>
                                                )}
                                    </div>
                                    
                                            {(() => {
                                                // Show date: startDate for assignments, or dedicatedPost.created_at for dedicated posts
                                                const dateToShow = dashboardData.assignment.startDate || 
                                                    (dashboardData.assignment.dedicatedPost?.created_at);
                                                if (dateToShow) {
                                                    return (
                                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                        <div className="text-sm text-gray-600 mb-1">Depuis le</div>
                                        <div className="font-semibold text-gray-900">
                                                                {new Date(dateToShow).toLocaleDateString('en-GB', {
                                                                    day: '2-digit',
                                                                    month: '2-digit',
                                                                    year: 'numeric'
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </div>
                                    );
                                } else {
                                    return (
                                <div className="text-center py-8">
                                    <FontAwesomeIcon icon={faPhone} className="text-4xl text-gray-300 mb-3" />
                                    <p className="text-gray-500">Aucun poste actuellement assigné</p>
                                    <p className="text-xs text-gray-400 mt-1">Contactez votre responsable pour obtenir un nouveau poste</p>
                                </div>
                                    );
                                }
                            })()}
                        </div>

                        {/* Control Ratings Section */}
                        {controlRatings && (controlRatings.global.average_rating !== null || (controlRatings.per_survey && controlRatings.per_survey.length > 0)) && (
                            <div className="lg:col-span-12 bg-white border border-gray-200 rounded-xl p-6 shadow-sm mb-6" style={{ overflow: 'visible' }}>
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="text-xl font-bold text-gray-900">Note de Contrôle</h3>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Global Rating */}
                                    {controlRatings.global.average_rating !== null && (
                                        <div className="bg-white rounded-lg p-4 sm:p-8 shadow-md border-2 border-red-600">
                                            <div className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">Note Globale</div>
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                                                <div className="flex-1 min-w-0">
                                                    <div className="scale-110 sm:scale-125 origin-left mb-2">
                                                        {renderStarRating(controlRatings.global.average_rating_percentage, controlRatings.global.average_rating, controlRatings.global.total_checks, false)}
                                                    </div>
                                                </div>
                                                <div className="text-left sm:text-right flex-shrink-0">
                                                    <div className="text-xl sm:text-2xl font-bold text-gray-900">
                                                        {controlRatings.global.average_rating.toFixed(1)}/5
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Per Survey Ratings */}
                                    {controlRatings.per_survey && controlRatings.per_survey.length > 0 && (
                                        <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border border-gray-200">
                                            <div className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">Par Enquête</div>
                                            <div className="space-y-3 max-h-64 overflow-y-auto">
                                                {controlRatings.per_survey.map((survey) => (
                                                    <div key={survey.survey_id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 pb-3 border-b border-gray-100 last:border-0">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-xs sm:text-sm font-medium text-gray-900 truncate break-words">
                                                                {survey.survey_title}
                                                            </div>
                                                            <div className="mt-1">
                                                                {renderStarRating(survey.average_rating_percentage, survey.average_rating, survey.total_checks, false)}
                                                            </div>
                                                        </div>
                                                        <div className="text-left sm:text-right sm:ml-4 flex-shrink-0">
                                                            <div className="text-base sm:text-lg font-bold text-gray-900">
                                                                {survey.average_rating.toFixed(1)}/5
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Performance Score Section */}
                        {interviewer && interviewer.accuracy_rating !== null && interviewer.accuracy_rating !== undefined && (
                            <div className="lg:col-span-12 bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-xl font-bold text-gray-900">Score de Performance</h3>
                                        <button
                                            onClick={() => setShowScoreDetails(!showScoreDetails)}
                                            className="text-gray-500 hover:text-red-600 transition-colors focus:outline-none"
                                            title="Afficher les détails du calcul"
                                        >
                                            <FontAwesomeIcon icon={faInfoCircle} className="text-sm" />
                                        </button>
                                    </div>
                                    {interviewer.score_updated_at && (
                                        <span className="text-xs text-gray-500">
                                            Mis à jour: {new Date(interviewer.score_updated_at).toLocaleDateString('fr-FR')}
                                        </span>
                                    )}
                                </div>
                                
                                {/* Expandable Details Section */}
                                {showScoreDetails && (
                                    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                        <div className="flex items-start justify-between mb-3">
                                            <h4 className="text-sm font-semibold text-gray-900">Détails du Calcul du Score</h4>
                                            <button
                                                onClick={() => setShowScoreDetails(false)}
                                                className="text-gray-500 hover:text-gray-700"
                                            >
                                                <FontAwesomeIcon icon={faChevronUp} className="text-xs" />
                                            </button>
                                        </div>
                                        <div className="text-sm text-gray-700 space-y-3">
                                            <div>
                                                <p className="font-semibold mb-1">Formule Principale:</p>
                                                <p className="ml-4">Score Global = (60% × Productivité combinée) + (40% × Qualité combinée)</p>
                                            </div>
                                            <div>
                                                <p className="font-semibold mb-1">1. Composante Productivité (60%):</p>
                                                <p className="ml-4 mb-1">• Basée sur le classement en percentile des complétions</p>
                                                <p className="ml-4 mb-1">• Formule: Productivité = (Nombre d'enquêteurs avec moins de complétions / Nombre total d'enquêteurs actifs avec complétions) × 100</p>
                                                <p className="ml-4 mb-1">• Compare votre nombre de complétions avec tous les enquêteurs actifs</p>
                                                <p className="ml-4">• Score de 0 à 100 selon votre position relative (percentile)</p>
                                            </div>
                                            <div>
                                                <p className="font-semibold mb-1">2. Composante Qualité (40%):</p>
                                                <p className="ml-4 mb-1">• Basée sur les points de pénalité reçus</p>
                                                <p className="ml-4 mb-1">• Formule: Qualité = max(0, 100 - (points_pénalité / tolérance) × 100)</p>
                                                <p className="ml-4 mb-1">• Tolérance 6 mois: 90 points | Tolérance 18 mois: 270 points</p>
                                                <p className="ml-4">• Plus vous avez de pénalités, plus votre score de qualité diminue</p>
                                            </div>
                                            <div>
                                                <p className="font-semibold mb-1">3. Fenêtres Temporelles:</p>
                                                <p className="ml-4 mb-1">• Fenêtre courte: 6 derniers mois (180 jours) - Poids: 70%</p>
                                                <p className="ml-4 mb-1">• Fenêtre longue: 18 derniers mois (540 jours) - Poids: 30%</p>
                                                <p className="ml-4">• Les performances récentes ont plus d'impact que les performances anciennes</p>
                                            </div>
                                            <div>
                                                <p className="font-semibold mb-1">4. Calcul Final:</p>
                                                <p className="ml-4 mb-1">• Productivité combinée = (70% × Productivité 6 mois) + (30% × Productivité 18 mois)</p>
                                                <p className="ml-4 mb-1">• Qualité combinée = (70% × Qualité 6 mois) + (30% × Qualité 18 mois)</p>
                                                <p className="ml-4">• Score Global = (60% × Productivité combinée) + (40% × Qualité combinée)</p>
                                            </div>
                                            <div className="pt-2 border-t border-blue-200">
                                                <p className="text-xs text-gray-600 italic">
                                                    Note: Un minimum de 1 complétion est requis pour calculer le score. 
                                                    Le score est mis à jour automatiquement lors des calculs périodiques et après chaque paiement.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {/* Overall Score - Circular Gauge */}
                                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 flex flex-col items-center justify-center">
                                        <div className="text-sm text-gray-600 mb-4">Score Global</div>
                                        <div className="relative w-40 h-40">
                                            {/* SVG Circular Gauge */}
                                            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 120 120">
                                                {/* Background circle */}
                                                <circle
                                                    cx="60"
                                                    cy="60"
                                                    r="54"
                                                    fill="none"
                                                    stroke="#e5e7eb"
                                                    strokeWidth="12"
                                                />
                                                {/* Progress circle */}
                                                <circle
                                                    cx="60"
                                                    cy="60"
                                                    r="54"
                                                    fill="none"
                                                    stroke={
                                                        interviewer.accuracy_rating >= 80 ? '#16a34a' :
                                                        interviewer.accuracy_rating >= 60 ? '#ca8a04' :
                                                        '#dc2626'
                                                    }
                                                    strokeWidth="12"
                                                    strokeLinecap="round"
                                                    strokeDasharray={`${2 * Math.PI * 54}`}
                                                    strokeDashoffset={`${2 * Math.PI * 54 * (1 - interviewer.accuracy_rating / 100)}`}
                                                    style={{
                                                        transition: 'stroke-dashoffset 1.5s ease-in-out',
                                                    }}
                                                />
                                            </svg>
                                            {/* Center text */}
                                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                                <span className="text-4xl font-bold text-gray-900">
                                                    {Math.round(interviewer.accuracy_rating)}
                                                </span>
                                                <span className="text-sm text-gray-500">/ 100</span>
                                            </div>
                                        </div>
                                        <div className="text-xs text-gray-500 mt-4">Performance globale</div>
                                    </div>
                                    
                                    {/* Productivity Component */}
                                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 flex flex-col items-center justify-center">
                                        <div className="text-sm text-gray-600 mb-4">Productivité</div>
                                        <div className="relative w-40 h-40">
                                            {/* SVG Circular Gauge */}
                                            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 120 120">
                                                {/* Background circle */}
                                                <circle
                                                    cx="60"
                                                    cy="60"
                                                    r="54"
                                                    fill="none"
                                                    stroke="#e5e7eb"
                                                    strokeWidth="12"
                                                />
                                                {/* Progress circle */}
                                                <circle
                                                    cx="60"
                                                    cy="60"
                                                    r="54"
                                                    fill="none"
                                                    stroke="#2563eb"
                                                    strokeWidth="12"
                                                    strokeLinecap="round"
                                                    strokeDasharray={`${2 * Math.PI * 54}`}
                                                    strokeDashoffset={`${2 * Math.PI * 54 * (1 - (interviewer.prod_component || 0) / 100)}`}
                                                    style={{
                                                        transition: 'stroke-dashoffset 1.5s ease-in-out 0.3s',
                                                    }}
                                                />
                                            </svg>
                                            {/* Center text */}
                                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                                <span className="text-4xl font-bold text-blue-600">
                                                    {interviewer.prod_component !== null ? Math.round(interviewer.prod_component) : '-'}
                                                </span>
                                                <span className="text-sm text-gray-500">/ 100</span>
                                            </div>
                                        </div>
                                        <div className="text-xs text-gray-500 mt-4">Basé sur les complétions</div>
                                    </div>
                                    
                                    {/* Quality Component */}
                                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 flex flex-col items-center justify-center">
                                        <div className="text-sm text-gray-600 mb-4">Qualité</div>
                                        <div className="relative w-40 h-40">
                                            {/* SVG Circular Gauge */}
                                            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 120 120">
                                                {/* Background circle */}
                                                <circle
                                                    cx="60"
                                                    cy="60"
                                                    r="54"
                                                    fill="none"
                                                    stroke="#e5e7eb"
                                                    strokeWidth="12"
                                                />
                                                {/* Progress circle */}
                                                <circle
                                                    cx="60"
                                                    cy="60"
                                                    r="54"
                                                    fill="none"
                                                    stroke="#16a34a"
                                                    strokeWidth="12"
                                                    strokeLinecap="round"
                                                    strokeDasharray={`${2 * Math.PI * 54}`}
                                                    strokeDashoffset={`${2 * Math.PI * 54 * (1 - (interviewer.qual_component || 0) / 100)}`}
                                                    style={{
                                                        transition: 'stroke-dashoffset 1.5s ease-in-out 0.6s',
                                                    }}
                                                />
                                            </svg>
                                            {/* Center text */}
                                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                                <span className="text-4xl font-bold text-green-600">
                                                    {interviewer.qual_component !== null ? Math.round(interviewer.qual_component) : '-'}
                                                </span>
                                                <span className="text-sm text-gray-500">/ 100</span>
                                            </div>
                                        </div>
                                        <div className="text-xs text-gray-500 mt-4">Basé sur les pénalités</div>
                                    </div>
                                </div>
                                
                            </div>
                        )}

                        {/* Interview Lines Statistics */}
                        {dashboardData.interviewLines.total > 0 && (
                            <div className="lg:col-span-12 bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="text-xl font-bold text-gray-900">Statistiques des Questionnaires CATI</h3>
                                    <FontAwesomeIcon icon={faClipboardCheck} className="text-xl text-gray-400" />
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {/* Total */}
                                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="text-sm text-gray-600">Total</div>
                                            <FontAwesomeIcon icon={faTasks} className="text-gray-400" />
                                        </div>
                                        <div className="text-3xl font-bold text-gray-900">
                                            {dashboardData.interviewLines.total}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-2">Lignes assignées</div>
                                    </div>

                                    {/* Completed */}
                                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="text-sm text-gray-600">Complétées</div>
                                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />
                                        </div>
                                        <div className="text-3xl font-bold text-green-600">
                                            {dashboardData.interviewLines.completed}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-2">Lignes complétées</div>
                                    </div>

                                    {/* Pending */}
                                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="text-sm text-gray-600">En attente</div>
                                            <FontAwesomeIcon icon={faClock} className="text-yellow-500" />
                                        </div>
                                        <div className="text-3xl font-bold text-yellow-600">
                                            {dashboardData.interviewLines.pending}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-2">Lignes en attente</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Recent Activity */}
                        <div className="lg:col-span-12 bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-xl font-bold text-gray-900">Activité Récente</h3>
                                <FontAwesomeIcon icon={faClock} className="text-xl text-gray-400" />
                            </div>
                            
                            {dashboardData.recentActivity.length > 0 ? (
                                <div className="space-y-3">
                                    {dashboardData.recentActivity.slice(0, 5).map((activity, index) => (
                                        <div key={index} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                            <div className="flex items-center space-x-4 flex-1 min-w-0">
                                                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center border border-gray-200 flex-shrink-0">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="font-medium text-gray-900 truncate">
                                                        Enquête {activity.survey_title} - Poste #{activity.post_number}
                                                    </div>
                                                    <div className="text-sm text-gray-600">
                                                        Complétée le {new Date(activity.date).toLocaleDateString('fr-FR')}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-left sm:text-right flex-shrink-0">
                                                <div className="font-semibold text-green-600 whitespace-nowrap">
                                                    +{activity.completes} complétés
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-gray-500">
                                    <FontAwesomeIcon icon={faClipboardList} className="text-4xl text-gray-300 mb-3" />
                                    <p>Aucune activité récente</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CatiInterviewerDashboard;
